import { Info } from 'lucide-react'
import { cn } from '@/lib/utils'
import { MONO, fmt } from './format'
import type { ProfitLossData } from '../types'

/**
 * Consolidated net-profit summary (€) — the group bottom line, month by month.
 *
 * Each entity is shown on its own statutory result: US net (USD) converted at that
 * month's locked rate, plus Spain's net (EUR). One consolidation entry is then made
 * — PC credit consumed. That is Planning Center work delivered against pre-paid
 * credit, which was already recognised as retainer revenue when the retainer was
 * sold, so it is a genuine double count at group level. It is eliminated ONCE here
 * rather than being netted off Spain's revenue, so each entity still ties to its own
 * books. Scoped to the fiscal YTD window where both entities have data.
 */
export function ConsolidatedSummary({ report, ytdStart }: { report: ProfitLossData; ytdStart: number }) {
  const months = report.months.slice(ytdStart)
  const usNet = report.data.us.totals.netProfit
  const spNet = report.data.spain.totals.netProfit
  const pcMemo = report.entityMeta.spain?.pcCreditMemo
  const fx = report.fxRates

  let cum = 0
  const rows = months.map((m, k) => {
    const i = ytdStart + k
    const usE = fx[m] ? usNet[i] / fx[m] : 0
    const spE = spNet[i] ?? 0
    const pcE = pcMemo?.[i] ?? 0
    const grp = usE + spE - pcE
    cum += grp
    return { m, usE, spE, pcE, grp, cum }
  })
  const usYtd = rows.reduce((t, r) => t + r.usE, 0)
  const spYtd = rows.reduce((t, r) => t + r.spE, 0)
  const pcYtd = rows.reduce((t, r) => t + r.pcE, 0)
  const grpYtd = usYtd + spYtd - pcYtd
  const range = months.length ? `${months[0]} – ${months[months.length - 1]}` : ''

  const money = (v: number, opts: { strong?: boolean; signed?: boolean; emerald?: boolean } = {}) => {
    const tone = opts.signed
      ? v < 0
        ? 'text-red-600 dark:text-red-400'
        : opts.emerald
          ? 'text-emerald-700 dark:text-emerald-400'
          : 'text-stone-900 dark:text-stone-100'
      : 'text-stone-700 dark:text-stone-200'
    return (
      <td className={cn('whitespace-nowrap px-3 py-2 text-right text-xs', MONO, opts.strong && 'font-semibold', tone)}>
        {fmt(v, 'EUR')}
      </td>
    )
  }

  return (
    <div className="space-y-4">
      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="Consolidated net profit" value={grpYtd} sub={`${range} · group`} emerald />
        <StatCard label="US contribution" value={usYtd} sub="DSD USA LLC (→ €)" />
        <StatCard label="Spain contribution" value={spYtd} sub="Planning Center SL" />
        <StatCard label="PC credit eliminated" value={-pcYtd} sub="delivered against pre-paid credit" />
      </div>

      {/* Consolidation note */}
      <div className="flex items-start gap-2 rounded-xl border border-champagne-200 bg-champagne-50/60 px-4 py-3 text-sm text-champagne-800 dark:border-champagne-500/30 dark:bg-champagne-500/10 dark:text-champagne-200">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Group net = US net (converted at each month&apos;s locked rate) + Spain net − PC credit consumed. Each entity
          is shown on its own statutory result, so both tie to their own books. PC credit is Planning Center work
          delivered against pre-paid credit — already recognised as retainer revenue when the retainer was sold — so it
          is eliminated once here rather than netted off Spain&apos;s revenue. Intercompany invoicing between the two
          entities is a net-zero transfer with no markup and cancels in the group net.
        </p>
      </div>

      {/* Monthly net-profit table */}
      <div className="rounded-2xl border border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
        <div className="px-5 pt-5">
          <h3 className="text-sm font-semibold text-stone-800 dark:text-stone-100">Net profit by month · {range}</h3>
          <p className="mt-0.5 text-xs text-stone-400 dark:text-stone-500">
            All figures in € · US converted at each month&apos;s average rate
          </p>
        </div>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-y border-stone-100 dark:border-stone-800">
                <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 dark:text-stone-400">Month</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-stone-500 dark:text-stone-400">US net (€)</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-stone-500 dark:text-stone-400">Spain net (€)</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-stone-500 dark:text-stone-400">PC credit elim. (€)</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-stone-600 dark:text-stone-300">Group net (€)</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-stone-500 dark:text-stone-400">Cumulative (€)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.m} className="border-t border-stone-50 dark:border-stone-800/40">
                  <th scope="row" className="px-4 py-2 text-left text-xs font-medium text-stone-700 dark:text-stone-200">{r.m}</th>
                  {money(r.usE, { signed: true })}
                  {money(r.spE, { signed: true })}
                  {money(-r.pcE, { signed: true })}
                  {money(r.grp, { strong: true, signed: true, emerald: true })}
                  {money(r.cum, { signed: true })}
                </tr>
              ))}
              <tr className="border-t-2 border-stone-200 bg-stone-50/70 dark:border-stone-700 dark:bg-stone-800/40">
                <th scope="row" className="px-4 py-2 text-left text-sm font-bold text-stone-900 dark:text-white">{report.ytdYear} YTD</th>
                {money(usYtd, { strong: true, signed: true })}
                {money(spYtd, { strong: true, signed: true })}
                {money(-pcYtd, { strong: true, signed: true })}
                {money(grpYtd, { strong: true, signed: true, emerald: true })}
                {money(grpYtd, { strong: true, signed: true })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

function StatCard({ label, value, sub, emerald }: { label: string; value: number; sub?: string; emerald?: boolean }) {
  const tone = value < 0 ? 'text-red-600 dark:text-red-400' : emerald ? 'text-emerald-700 dark:text-emerald-400' : 'text-stone-900 dark:text-stone-100'
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 dark:border-stone-800 dark:bg-stone-900">
      <p className="text-xs font-medium text-stone-500 dark:text-stone-400">{label}</p>
      <p className={cn('mt-1 text-xl font-semibold', MONO, tone)}>{fmt(value, 'EUR')}</p>
      {sub && <p className="mt-0.5 text-[11px] text-stone-400 dark:text-stone-500">{sub}</p>}
    </div>
  )
}
